import { FormProvider, useForm } from "react-hook-form";
import { FormModal } from "@marekl/adminer/components";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { notifySuccess } from "@marekl/adminer/api";
import { BillingDetailsFields, emptyBillingDetails } from "../../components/BillingDetailsFields";
import { useCurrentCompany } from "../../lib/company";
import { useSaveContact, type Contact, type ContactInput } from "../../lib/queries/contacts";

interface ContactForm {
  billing_details: ReturnType<typeof emptyBillingDetails>;
}

/**
 * Create or edit an address-book entry. A changed entry is a new snapshot:
 * documents already issued keep printing the details they were issued with.
 */
export function ContactModal({
  contact,
  onClose,
  onSaved,
}: {
  contact?: Contact;
  onClose: () => void;
  onSaved?: (c: Contact) => void;
}) {
  const { id } = useCurrentCompany();
  const save = useSaveContact(id);
  const bd = emptyBillingDetails();
  if (contact) {
    for (const k of Object.keys(bd) as (keyof typeof bd)[]) {
      (bd as Record<string, unknown>)[k] = contact.billing_details[k] ?? bd[k];
    }
  }
  const form = useForm<ContactForm>({ mode: "onTouched", defaultValues: { billing_details: bd } });

  async function onSubmit(v: ContactForm) {
    try {
      const saved = await save.mutateAsync({ id: contact?.id, input: v as ContactInput });
      notifySuccess(contact ? "Kontakt byl uložen." : "Kontakt byl přidán.");
      onSaved?.(saved);
      onClose();
    } catch (err) {
      apiErrorToFormErrors<ContactForm>(err, form.setError, { fallbackMessage: "Uložení se nepovedlo." });
    }
  }

  return (
    <FormModal
      open
      title={contact ? "Kontakt" : "Nový kontakt"}
      width={720}
      busy={save.isPending}
      error={rootErrorMessage(form.formState.errors)}
      onSubmit={form.handleSubmit(onSubmit)}
      onClose={onClose}
    >
      <FormProvider {...form}>
          <BillingDetailsFields />
      </FormProvider>
    </FormModal>
  );
}
